import { environmentHttpClient } from "client/Api/httpClient";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";
import {
	DocumentActionConditionalAccessSettingsSaveModel,
	DocumentActionConditionalAccessSettingsModel,
	OfficeDocumentEditor
} from "client/Api/Documents/common";
import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";

export enum SharePointAuthenticationType {
	online = 0,
	activeDirectoryFederationServices = 1,
	oAuth2 = 4
}

export enum SiteAvailabilityFilterKind {
	None = 0,
	Whitelist = 1,
	Blacklist = 2
}

export type SiteAvailabilityViewModel = { kind: SiteAvailabilityFilterKind.None; }
	| { kind: SiteAvailabilityFilterKind.Whitelist; nodeIds: SharePointSettingsNodeId[]; }
	| { kind: SiteAvailabilityFilterKind.Blacklist; nodeIds: SharePointSettingsNodeId[]; };

export type DocumentActionPermissionsModel = {
	everyoneHasAccess: boolean;
	userIds: number[];
	userGroupIds: number[];
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export type OfficeAppsSettingsViewModel = {
	isOfficeWebAppsOpenTargetSettingVisible: boolean;
	officeWebAppOpenTarget: OfficeWebAppOpenTarget | null;
	defaultOfficeDocumentEditor: OfficeDocumentEditor;
	allowToOpenInLocalApp: boolean;
	openLocalConditionalAccessSettings: DocumentActionConditionalAccessSettingsModel;
	openLocalPermissions: DocumentActionPermissionsModel;
};

export enum SyncClient {
	None = 0,
	Groove = 1,
	OneDrive = 2
}

export enum TemplateAvailabilityKind {
	Company = 1,
	Library = 2
}

export type SharePointSettingsViewModel = {
	isEnabled: boolean;
	availableAuthenticationTypes: SharePointAuthenticationType[];
	authenticationType: SharePointAuthenticationType;
	url: string;
	syncClient: SyncClient;
	useFasterLoading: boolean;
	siteManagementSettings: SiteManagementSettingsViewModel;
	isDocumentViewStructureCustomizationEnabled: boolean;
	templateAvailabilityKind: TemplateAvailabilityKind;
	siteAvailability: SiteAvailabilityViewModel;
	officeAppsSettings: OfficeAppsSettingsViewModel;
	isSharePointViewerEnabled: boolean;
};

export type SiteManagementSettingsViewModel = {
	siteManagementEnabled: boolean;
	defaultDescription: string;
	language: number;
	permissions: PermissionsMode;
	overrideDescription: boolean;
	overrideLanguage: boolean;
	overridePermissions: boolean;
	availableLanguages: SiteManagementLanguage[];
};

export enum PermissionsMode {
	Inherit = 1,
	Unique = 2
}

export type SiteManagementLanguage = {
	id: number;
	name: string;
};

export enum ContainerType {
	site = 1,
	library = 2
}

export type SharePointSettingsNode = {
	id: SharePointSettingsNodeId;
	title: string;
	containerType: ContainerType;
	subItems: SharePointSettingsNode[];
};

export type SharePointSettingsNodeId = {
	siteCollectionId: string | null;
	objectId: string;
};

export type SiteAvailabilitySaveModel = { kind: SiteAvailabilityFilterKind.None; }
	| { kind: SiteAvailabilityFilterKind.Whitelist; nodeIds: readonly SharePointSettingsNodeId[]; }
	| { kind: SiteAvailabilityFilterKind.Blacklist; nodeIds: readonly SharePointSettingsNodeId[]; };

export type DocumentActionPermissionsSaveModel = { everyoneHasAccess: true; } | {
	everyoneHasAccess: false;
	userIds: number[];
	userGroupIds: number[];
};

export type OfficeAppsSavingModel = {
	officeWebAppOpenTarget: OfficeWebAppOpenTarget;
	defaultOfficeDocumentEditor: OfficeDocumentEditor;
	allowToOpenInLocalApp: boolean;
	openLocalPermissions: DocumentActionPermissionsSaveModel;
	openLocalConditionalAccessSettings: DocumentActionConditionalAccessSettingsSaveModel;
};

export type SharePointSettingsSavingModel = {
	authenticationType: SharePointAuthenticationType | null;
	url: string;
	syncClient: SyncClient;
	useFasterLoading: boolean;
	siteManagementSettings: SiteManagementSettingsSavingModel;
	isDocumentViewStructureCustomizationEnabled: boolean;
	siteAvailability: OptionalModel<SiteAvailabilitySaveModel>;
	templateAvailabilityKind: TemplateAvailabilityKind;
	officeAppsSettings: OfficeAppsSavingModel;
	isSharePointViewerEnabled: boolean;
};

export type SiteManagementSettingsSavingModel = {
	siteManagementEnabled: boolean;
	defaultDescription: string;
	permissions: PermissionsMode;
	language: number;
	overrideDescription: boolean;
	overrideLanguage: boolean;
	overridePermissions: boolean;
};

export type DocumentContainerChildModel = {
	id: SharePointSettingsNodeId;
	title: string;
	containerType: ContainerType;
};

export function getSharePointSettingsViewModel(
	signal?: AbortSignal
): Promise<SharePointSettingsViewModel> {
	return environmentHttpClient.get<SharePointSettingsViewModel>(
		"UiApi/SharePointSettings/GetSharePointSettingsViewModel",
		undefined,
		{ signal }
	);
}

export function tryDetectSharePointUrl(
	signal?: AbortSignal
): Promise<string | null> {
	return environmentHttpClient.get<string | null>(
		"UiApi/SharePointSettings/TryDetectSharePointUrl",
		undefined,
		{ signal }
	);
}

type LoadSharePointHierarchyModel = {
	authenticationType: SharePointAuthenticationType;
	url: string;
	useFasterLoading: boolean;
};

export function loadHierarchy(
	model: LoadSharePointHierarchyModel,
	signal?: AbortSignal
): Promise<readonly SharePointSettingsNode[]> {

	return environmentHttpClient.get<readonly SharePointSettingsNode[]>(
		"UiApi/SharePointSettings/LoadHierarchy",
		model,
		{ signal }
	);
}

export function saveSettings(
	sharePointSettingsSavingModel: SharePointSettingsSavingModel
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/SharePointSettings/SaveSettings",
		sharePointSettingsSavingModel
	);
}

export function disable(
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		"UiApi/SharePointSettings/Disable",
		undefined,
		{ signal }
	);
}

export type SaveSharePointUrlModel = {
	authenticationType: SharePointAuthenticationType | null;
	sharePointUrl: string;
	shouldDisableSiteAvailability: boolean;
};

export enum ConfigureSharePointUrlResult {
	Successful = 1,
	NeedToDisableSiteAvailability = 2
}

export function configureSharePointUrl(
	model: SaveSharePointUrlModel,
	signal?: AbortSignal
): Promise<ConfigureSharePointUrlResult> {
	return environmentHttpClient.put<ConfigureSharePointUrlResult>(
		"UiApi/SharePointSettings/ConfigureSharePointUrl",
		model,
		{ signal }
	);
}
